import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Loader2 } from "lucide-react";
import { ReviewContent } from "@/components/review/ReviewContent";
import { ReportDialog } from "@/components/review/ReportDialog";
import { EditReviewContent } from "@/components/review/EditReviewContent";
import { AdminReviewActions } from "@/components/review/AdminReviewActions";
import { useIsMobile } from "@/hooks/use-mobile";
import { useReviewData } from "@/hooks/useReviewData";
import { useReviewActions } from "@/hooks/useReviewActions";
import type { ReviewFormData } from "@/components/ReviewForm";
import { useEffect } from "react";

export default function ReviewDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const isFromAdmin = location.pathname.includes('/admin') || location.state?.from?.includes('/admin');

  const handleBack = () => {
    const fromLocation = location.state?.from;
    if (fromLocation) {
      navigate(fromLocation);
    } else if (location.pathname.includes('/admin')) {
      navigate('/admin/reviews');
    } else {
      navigate('/');
    }
  };

  const {
    review,
    setReview,
    isLoading,
    isCurrentUserReview,
    isAdmin,
    userVoteType
  } = useReviewData(id);

  const {
    isDeleting,
    userVote,
    upvotes,
    downvotes,
    isVoting,
    isEditing,
    isSubmitting,
    isReported,
    setIsEditing,
    setUserVote,
    handleDelete,
    handleReport,
    handleDismissReport,
    handleUpvote,
    handleDownvote,
    handleSubmitEdit
  } = useReviewActions(review, handleBack);

  useEffect(() => {
    if (userVoteType) {
      setUserVote(userVoteType);
    }
  }, [userVoteType, setUserVote]);

  const handleFormDataChange = (newData: Partial<ReviewFormData>) => {
    if (review) {
      setReview({ ...review, ...newData });
    }
  };

  const handleAirportSelect = (airport: { name: string; location: string; latitude?: string; longitude?: string }) => {
    if (review) {
      setReview({
        ...review,
        airport_name: airport.name,
        airport_location: airport.location,
        latitude: airport.latitude || review.latitude,
        longitude: airport.longitude || review.longitude
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (review) {
      const formData: ReviewFormData = {
        airport_name: review.airport_name,
        airport_location: review.airport_location,
        review_text: review.review_text,
        film_friendly_score: review.film_friendly_score,
        hand_check_success: review.hand_check_success,
        latitude: review.latitude?.toString() || "",
        longitude: review.longitude?.toString() || "",
        terminal: review.terminal || ""
      };
      return handleSubmitEdit(formData);
    }
    return Promise.resolve();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (id === 'new' || !review) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold mb-2">
            {id === 'new' ? "Create New Review" : "Review not found"}
          </h2>
          <Link to="/">
            <Button>Return Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`container mx-auto ${isMobile ? 'px-3 pb-20' : 'px-4'} py-4`}>
      <div className="max-w-3xl mx-auto">
        <div className="space-y-4">
          <Button 
            variant="outline" 
            size="icon" 
            onClick={handleBack}
            className={isMobile ? 'mb-2' : ''}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          
          <Card className={isMobile ? 'p-4' : 'p-8'}>
            {isEditing ? (
              <EditReviewContent
                review={review}
                isSubmitting={isSubmitting}
                onSubmit={handleSubmit}
                onCancel={() => setIsEditing(false)}
                onAirportSelect={handleAirportSelect}
                onFormDataChange={handleFormDataChange}
              />
            ) : (
              <ReviewContent
                review={review}
                isCurrentUserReview={isCurrentUserReview}
                upvotes={upvotes}
                downvotes={downvotes}
                isVoting={isVoting}
                isDeleting={isDeleting}
                onUpvote={handleUpvote}
                onDownvote={handleDownvote}
                onDelete={handleDelete}
                onEdit={() => setIsEditing(true)}
                showDelete={isCurrentUserReview || (isAdmin && !isFromAdmin)}
                showEdit={isCurrentUserReview}
              />
            )}
          </Card>

          {isFromAdmin && isAdmin && isReported && (
            <AdminReviewActions
              onDelete={handleDelete}
              onDismiss={handleDismissReport}
              isDeleting={isDeleting}
              isMobile={isMobile}
            />
          )}

          {!isCurrentUserReview && !isFromAdmin && (
            <div className="flex justify-center mt-4">
              <ReportDialog onReport={handleReport} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
